import React, {
  Children,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfigProvider, Form, Input, Space } from "antd";
import PhoneInputWithCountrySelect from "react-phone-number-input/max";
import { parseIncompletePhoneNumber } from "libphonenumber-js/max";
import { LANGUAGE } from "services/general";
import CountrySelect from "./CountrySelect";
import {
  countryNames,
  dialCodeOf,
  e164ToStored,
  isValidE164,
  pastedToE164,
  phoneCountryOf,
  storedToE164,
  trunkPrefixOf,
} from "./phoneNumber";
import { STATUS_ERROR } from "./constants";

/*
 * The control a phone `Form.Item` renders: `react-phone-number-input` doing the
 * parsing, formatting and caret work, drawn with AntD's own Input and Select so
 * it carries the same height, borders, focus ring and error state as the fields
 * around it.
 */

/*
 * The number box. The library formats as you type and keeps the caret in place,
 * which needs the DOM <input/> — AntD's Input ref is an object wrapping it, so
 * the element itself is handed over.
 *
 * The box opens with the dialling code of `phoneCountry` ("+966"), which only
 * the country picker can change. The library resets a code that is typed over —
 * taking the whole number with it — so the box keeps the user out of it:
 *  - the caret and any selection are held after the code ("+966 |"), so a click
 *    on the code, Home or select-all still edit only the number;
 *  - Backspace at the start of the number stops there instead of eating into the
 *    code;
 *  - the trunk 0 people dial at home is dropped when the number starts:
 *    "+966 50…", not "+966 050…";
 *  - a local number pasted over the number ("0501234567") goes in after the
 *    code; a number of another country is refused, since the code is the
 *    picker's to change.
 */
const NumberInput = forwardRef(function NumberInput(
  { phoneCountry, onFocus, onKeyDown, onPaste, onSelect, ...props },
  ref
) {
  const antdRef = useRef(null);
  useImperativeHandle(ref, () => antdRef.current?.input);

  const dialCode = dialCodeOf(phoneCountry);
  const trunkPrefix = trunkPrefixOf(phoneCountry);

  // Where the number starts: after the code and the space the library puts
  // after it once a digit follows ("+966 5…").
  const numberStart = (input) =>
    input.value.startsWith(dialCode)
      ? dialCode.length + (input.value[dialCode.length] === " " ? 1 : 0)
      : 0;

  // Whatever is selected from the number's start to the end — the whole number,
  // or the empty space after a bare code.
  const replacesNumber = (input) =>
    input.selectionEnd === input.value.length &&
    input.selectionStart <= numberStart(input);

  // Writes `text` into the box the way typing would: the library reads the box
  // on change and formats it.
  const write = (input, text) => {
    input.value = text;
    input.setSelectionRange(text.length, text.length);
    props.onChange({ target: input });
  };

  const holdOutOfCode = (input) => {
    const start = numberStart(input);
    if (input.selectionStart < start) {
      input.setSelectionRange(start, Math.max(start, input.selectionEnd));
    }
  };

  // `beforeinput` rather than `keydown`: phone keyboards and IMEs often send no
  // key for a typed digit, but every browser reports the text about to go in —
  // one digit for a key press, a run of them from autofill or voice input.
  // React has no `beforeinput` of its own, so the listener goes on the DOM
  // input, and it is rebound after every render rather than once on mount: it
  // always sees this render's code and follows the element if AntD ever swaps
  // it, where a listener bound once would go stale and quietly stop working.
  useEffect(() => {
    const input = antdRef.current?.input;
    if (!input) return undefined;
    const handleBeforeInput = (event) => {
      if (event.inputType !== "insertText" || !event.data) return;
      // Arabic-Indic digits count as digits.
      const digits = parseIncompletePhoneNumber(event.data);
      if (!dialCode || !/^\d+$/.test(digits)) return;
      if (input.value !== "" && !replacesNumber(input)) return;
      event.preventDefault();
      const local =
        trunkPrefix && digits.startsWith(trunkPrefix)
          ? digits.slice(trunkPrefix.length)
          : digits;
      write(input, dialCode + local);
    };
    input.addEventListener("beforeinput", handleBeforeInput);
    return () => input.removeEventListener("beforeinput", handleBeforeInput);
  });

  const handleFocus = (event) => {
    onFocus?.(event);
    const input = event.target;
    // After the click or the tab has placed the caret, not before.
    setTimeout(() => holdOutOfCode(input));
  };

  const handleSelect = (event) => {
    onSelect?.(event);
    holdOutOfCode(event.target);
  };

  const handleKeyDown = (event) => {
    const input = event.target;
    const atNumberStart =
      input.selectionStart === input.selectionEnd &&
      input.selectionStart <= numberStart(input);
    if (event.key === "Backspace" && atNumberStart) {
      event.preventDefault();
      return;
    }
    onKeyDown?.(event);
  };

  const handlePaste = (event) => {
    const input = event.target;
    if (!replacesNumber(input)) {
      onPaste?.(event);
      return;
    }
    event.preventDefault();
    const e164 = pastedToE164(
      event.clipboardData.getData("text"),
      phoneCountry
    );
    if (e164?.startsWith(dialCode)) write(input, e164);
  };

  return (
    <Input
      ref={antdRef}
      {...props}
      onFocus={handleFocus}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  );
});

// The library renders the country select and the number input side by side;
// `Space.Compact` joins them into one control. The surrounding Form.Item's state
// belongs to the number box alone — without the nameless `noStyle` item the
// select would draw a second validation icon — and is mirrored onto the
// container as a class, so the CSS can colour both halves' borders together.
function PhoneFieldContainer({ children, className, style }) {
  const [countrySelect, numberInput] = Children.toArray(children);
  const { status } = Form.Item.useStatus();
  return (
    <div
      className={status ? `${className} phone-field--${status}` : className}
      style={style}
    >
      <Space.Compact block>
        <Form.Item noStyle hasFeedback={false} validateStatus="">
          {countrySelect}
        </Form.Item>
        {numberInput}
      </Space.Compact>
    </div>
  );
}

/**
 * `value` / `onChange` speak the form's language — international digits without
 * the "+" — and `onBlur` / `onRevalidate` are injected by the parent `Form.Item`
 * (see `validateTrigger` in ../PhoneNumInpt.jsx).
 */
export default function PhoneField({
  value,
  onChange,
  onBlur,
  onRevalidate,
  defaultCountry,
  ...rest
}) {
  // What this field last emitted. While the form holds it unchanged, the control
  // keeps its own E.164 rather than re-reading the digits — re-reading a partial
  // number like "96650" as national digits would rewrite it mid-typing.
  const lastEmitted = useRef({ stored: undefined, e164: undefined });

  const e164 = useMemo(
    () =>
      value === lastEmitted.current.stored
        ? lastEmitted.current.e164
        : storedToE164(value, defaultCountry),
    [value, defaultCountry]
  );

  const [country, setCountry] = useState(
    () => phoneCountryOf(e164) ?? defaultCountry
  );
  const { status } = Form.Item.useStatus();

  const handleChange = (next) => {
    const stored = e164ToStored(next);
    lastEmitted.current = { stored, e164: next };
    onChange?.(stored);
    // Errors appear on blur, but a shown error clears the moment the number
    // becomes valid. Only while one is shown: revalidating also runs the
    // screen's server check (`validatePhone`), which otherwise waits for blur
    // rather than firing on every keystroke of a valid number.
    if (status === STATUS_ERROR && isValidE164(next)) onRevalidate?.();
  };

  return (
    // A phone number reads left to right on either page: the dialling code
    // first, then the number. Only the control is LTR — the label and the
    // error message keep the page's direction.
    <ConfigProvider direction="ltr">
      <PhoneInputWithCountrySelect
        {...rest}
        className="phone-field"
        value={e164}
        onChange={handleChange}
        onBlur={onBlur}
        defaultCountry={defaultCountry}
        // The dialling code lives in the box, "+966 50 123 4567", and is
        // fixed: only the country picker changes it. The user types the
        // number after it.
        international
        countryCallingCodeEditable={false}
        addInternationalOption={false}
        limitMaxLength
        labels={countryNames()}
        locales={LANGUAGE}
        numberInputProps={{ phoneCountry: country }}
        countrySelectProps={{ onShownCountry: setCountry }}
        countrySelectComponent={CountrySelect}
        inputComponent={NumberInput}
        containerComponent={PhoneFieldContainer}
      />
    </ConfigProvider>
  );
}
